import { SVG_NS, attachHoverListeners, observeResize } from './shared';

/**
 * Configuration for a pie chart instance.
 */
export interface PieChartConfig {
  /** One numeric value per slice. */
  data: number[];
  /** Label per slice. Must match data length. */
  labels: string[];
  /** Color per slice. Defaults to Thruscan brand palette. */
  colors?: string[];
  /** Chart width in pixels. Defaults to container's clientWidth or 350. */
  width?: number;
  /** Chart height in pixels. Defaults to width (square). */
  height?: number;
  /** Donut mode (hollow center). Default false. */
  donut?: boolean;
  /** Donut ring thickness in pixels. Default 60. */
  donutWidth?: number;
  /** Show labels outside slices. Default true. */
  showLabels?: boolean;
  /** Enable hover tooltip. Default true. */
  hover?: boolean;
}

var TWO_PI = Math.PI * 2;

/**
 * Creates a high-performance SVG+HTML hybrid pie/donut chart inside the specified container.
 */
export function NanoPieChart(containerId: string, config: PieChartConfig): void {
  var data = config.data;
  var labels = config.labels;
  var colors = config.colors || ['#215CAF', '#007894', '#00C9A7', '#DE4437', '#F69C3D', '#0d3a6e', '#003d4a', '#006651'];
  var isDonut = config.donut === true;
  var donutWidth = config.donutWidth !== undefined ? config.donutWidth : 60;
  var showLabels = config.showLabels !== false;
  var hoverEnabled = config.hover !== false;

  var container = document.getElementById(containerId)!;
  if (!container) return;
  var chartWidth = config.width || container.clientWidth || 350;
  var chartHeight = config.height || chartWidth;
  var sliceCount = data.length;

  var centerX = chartWidth / 2;
  var centerY = chartHeight / 2;
  var outerR = Math.min(centerX, centerY) - (showLabels ? 40 : 10);
  var innerR = isDonut ? Math.max(outerR - donutWidth, 0) : 0;

  // ── Phase 1: Pure math (zero DOM) ──
  var i: number;
  var total = 0;
  for (i = 0; i < sliceCount; i++) total += data[i];

  // Pre-compute per-slice: startAngle, endAngle, percentage
  var startAngles = new Float64Array(sliceCount);
  var endAngles = new Float64Array(sliceCount);
  var percentages = new Float64Array(sliceCount);
  var angle = -Math.PI / 2; // start at 12 o'clock

  for (i = 0; i < sliceCount; i++) {
    startAngles[i] = angle;
    var sliceAngle = total > 0 ? (data[i] / total) * TWO_PI : 0;
    percentages[i] = total > 0 ? (data[i] / total) * 100 : 0;
    angle += sliceAngle;
    endAngles[i] = angle;
  }

  // ── Phase 2: Build SVG string ──
  var svgMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="' + chartWidth + '" height="' + chartHeight + '" viewBox="0 0 ' + chartWidth + ' ' + chartHeight + '" class="chart">';

  for (i = 0; i < sliceCount; i++) {
    // Skip tiny slices (<0.5°) to avoid rendering artifacts
    if (endAngles[i] - startAngles[i] < 0.009) continue;

    var color = colors[i % colors.length];
    var cos1 = Math.cos(startAngles[i]), sin1 = Math.sin(startAngles[i]);
    var cos2 = Math.cos(endAngles[i]), sin2 = Math.sin(endAngles[i]);
    var largeArc = endAngles[i] - startAngles[i] > Math.PI ? 1 : 0;

    var ox1 = centerX + outerR * cos1, oy1 = centerY + outerR * sin1;
    var ox2 = centerX + outerR * cos2, oy2 = centerY + outerR * sin2;

    var pathData: string;
    if (isDonut) {
      var ix1 = centerX + innerR * cos1, iy1 = centerY + innerR * sin1;
      var ix2 = centerX + innerR * cos2, iy2 = centerY + innerR * sin2;
      // Outer arc clockwise, line to inner, inner arc counter-clockwise, close
      pathData = 'M' + ox1.toFixed(2) + ',' + oy1.toFixed(2)
        + 'A' + outerR + ',' + outerR + ' 0 ' + largeArc + ' 1 ' + ox2.toFixed(2) + ',' + oy2.toFixed(2)
        + 'L' + ix2.toFixed(2) + ',' + iy2.toFixed(2)
        + 'A' + innerR + ',' + innerR + ' 0 ' + largeArc + ' 0 ' + ix1.toFixed(2) + ',' + iy1.toFixed(2)
        + 'Z';
    } else {
      // Move to center, line to arc start, arc, close
      pathData = 'M' + centerX.toFixed(2) + ',' + centerY.toFixed(2)
        + 'L' + ox1.toFixed(2) + ',' + oy1.toFixed(2)
        + 'A' + outerR + ',' + outerR + ' 0 ' + largeArc + ' 1 ' + ox2.toFixed(2) + ',' + oy2.toFixed(2)
        + 'Z';
    }

    svgMarkup += '<path d="' + pathData + '" fill="' + color + '" class="chart-slice"/>';
  }

  svgMarkup += '</svg>';

  // ── Phase 3: Build HTML label overlay ──
  var labelMarkup = '<div class="chart-labels">';

  if (showLabels) {
    var labelR = outerR + 20;
    for (i = 0; i < sliceCount; i++) {
      // Skip labels for tiny slices (<5%)
      if (percentages[i] < 5) continue;

      var midAngle = (startAngles[i] + endAngles[i]) / 2;
      var lx = centerX + labelR * Math.cos(midAngle);
      var ly = centerY + labelR * Math.sin(midAngle);

      labelMarkup += '<span style="left:' + lx.toFixed(1) + 'px;top:' + ly.toFixed(1)
        + 'px;transform:translate(-50%,-50%);text-align:center">'
        + labels[i] + ' ' + percentages[i].toFixed(0) + '%</span>';
    }
  }

  labelMarkup += '</div>';

  // ── Phase 4: Single innerHTML write ──
  container.style.cssText = 'position:relative;width:' + chartWidth + 'px;height:' + chartHeight + 'px';
  container.innerHTML = svgMarkup + labelMarkup;

  // ── Phase 5: Auto-resize ──
  if (!config.width) {
    observeResize(container, function () { NanoPieChart(containerId, config); });
  }

  // ── Phase 6: Lazy hover ──
  if (!hoverEnabled) return;

  var svgEl = container.firstChild as SVGSVGElement;
  var highlight: SVGPathElement | null = null;
  var tooltip: HTMLDivElement | null = null;
  var lastSlice = -1;

  function ensureHover() {
    if (!highlight) {
      highlight = document.createElementNS(SVG_NS, 'path') as SVGPathElement;
      highlight.setAttribute('class', 'chart-indicator');
      highlight.setAttribute('fill', 'none');
      highlight.setAttribute('stroke', '#fff');
      highlight.setAttribute('stroke-width', '3');
      highlight.style.display = 'none';
      svgEl.appendChild(highlight);
    }
    if (!tooltip) {
      tooltip = document.createElement('div') as HTMLDivElement;
      tooltip.className = 'chart-tooltip';
      container.appendChild(tooltip);
    }
  }

  attachHoverListeners(container, svgEl,
    function (mx, my) {
      var dx = mx - centerX, dy = my - centerY;
      var dist = Math.sqrt(dx * dx + dy * dy);

      // Outside pie or inside donut hole
      if (dist > outerR || dist < innerR) {
        if (highlight) highlight.style.display = 'none';
        if (tooltip) tooltip.style.opacity = '0';
        lastSlice = -1;
        return;
      }

      // Find slice by angle
      var hoverAngle = Math.atan2(dy, dx);
      // Normalize to match our startAngles range [-π/2, 3π/2)
      if (hoverAngle < -Math.PI / 2) hoverAngle += TWO_PI;

      var hitSlice = -1;
      for (var s = 0; s < sliceCount; s++) {
        if (hoverAngle >= startAngles[s] && hoverAngle < endAngles[s]) {
          hitSlice = s;
          break;
        }
      }

      if (hitSlice === -1) return;

      ensureHover();

      // Only rebuild highlight path if slice changed
      if (hitSlice !== lastSlice) {
        lastSlice = hitSlice;
        // Reuse the same arc path as the slice for the highlight outline
        var cos1 = Math.cos(startAngles[hitSlice]), sin1 = Math.sin(startAngles[hitSlice]);
        var cos2 = Math.cos(endAngles[hitSlice]), sin2 = Math.sin(endAngles[hitSlice]);
        var largeArc = endAngles[hitSlice] - startAngles[hitSlice] > Math.PI ? 1 : 0;
        var ox1 = centerX + outerR * cos1, oy1 = centerY + outerR * sin1;
        var ox2 = centerX + outerR * cos2, oy2 = centerY + outerR * sin2;

        var hPath: string;
        if (isDonut) {
          var ix1 = centerX + innerR * cos1, iy1 = centerY + innerR * sin1;
          var ix2 = centerX + innerR * cos2, iy2 = centerY + innerR * sin2;
          hPath = 'M' + ox1.toFixed(2) + ',' + oy1.toFixed(2)
            + 'A' + outerR + ',' + outerR + ' 0 ' + largeArc + ' 1 ' + ox2.toFixed(2) + ',' + oy2.toFixed(2)
            + 'L' + ix2.toFixed(2) + ',' + iy2.toFixed(2)
            + 'A' + innerR + ',' + innerR + ' 0 ' + largeArc + ' 0 ' + ix1.toFixed(2) + ',' + iy1.toFixed(2)
            + 'Z';
        } else {
          hPath = 'M' + centerX.toFixed(2) + ',' + centerY.toFixed(2)
            + 'L' + ox1.toFixed(2) + ',' + oy1.toFixed(2)
            + 'A' + outerR + ',' + outerR + ' 0 ' + largeArc + ' 1 ' + ox2.toFixed(2) + ',' + oy2.toFixed(2)
            + 'Z';
        }
        highlight!.setAttribute('d', hPath);
      }

      highlight!.style.display = '';

      // Tooltip near cursor
      tooltip!.innerHTML = '<b>' + labels[hitSlice] + '</b><br>'
        + data[hitSlice] + ' (' + percentages[hitSlice].toFixed(1) + '%)';
      tooltip!.style.transform = 'translate(' + mx + 'px,' + (my - 40) + 'px) translate(-50%,-100%)';
      tooltip!.style.opacity = '1';
    },
    function () {
      if (highlight) highlight.style.display = 'none';
      if (tooltip) tooltip.style.opacity = '0';
      lastSlice = -1;
    }
  );
}
